import { Component } from 'vue-property-decorator';
import Vue from 'vue';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import duration from 'dayjs/plugin/duration';
import { Meta, Customer, ResponseSingle, ULID, Subscription } from '@/store/models';
import Pagination from '@/components/Pagination';
import ConfirmationPopup from '@/components/ConfirmationPopup';
import customer from '@/store/modules/customer';
import { router } from '@/_helpers';
import notifier from '@/_helpers/notifier';
import Search from '@/components/Search';
import template from './Customer.vue';

dayjs.extend(relativeTime);
dayjs.extend(duration);
@Component({
	mixins: [template],
	components: {
		Pagination,
		ConfirmationPopup,
		Search
	},
	filters: {
		storage(amountInBytes: number): string {
			if (amountInBytes > 10000) {
				return `${amountInBytes / 1000000} GB`;
			}
			if (amountInBytes > 100) {
				return `${amountInBytes / 1000} MB`;
			}
			return `${amountInBytes} KB`;
		},
		scans(amountScans: number): string {
			if (amountScans > 100000) {
				return `${amountScans / 1000000} Miljoen`;
			}
			if (amountScans > 100) {
				return `${amountScans / 1000} Duizend`;
			}
			return `${amountScans}`;
		}
	}
})
export default class CustomerOverview extends Vue {
	loading: boolean = false;

	show: boolean = false;

	deleteId: string = '';

	searchWord: string = '';

	query: any;

	customers: Customer[] = [];

	mounted() {
		this.query = Object.assign({}, this.$route.query);
		this.searchWord = this.query.search as string;

		if (this.searchWord !== '' && typeof this.searchWord !== 'undefined') {
			const element = document.getElementById('field');
			if (element) {
				element.focus();
			}
		}

		this.getAll();
	}

	getTrialDaysLeft(trialEnd: string): number {
		// Trial Period days over
		const date1 = dayjs(trialEnd);
		const date2 = dayjs(Date.now());

		const trialLeft = Math.ceil(dayjs.duration(date1.diff(date2)).asDays());
		return trialLeft;
	}

	getStatusColor(cust: Customer): string {
		const { progress, subscriptions } = cust;

		if (subscriptions.length > 0) {
			return 'cyan darken-1';
		}

		// No subscription? Check if trial is running
		if (progress.verified) {
			return 'amber darken-1';
		}
		if (progress.address) {
			return 'lime darken-1';
		}
		if (progress.additionalInformation) {
			return 'green darken-1';
		}
		if (progress.permissionToPay) {
			return 'light-green darken-1';
		}

		return 'blue-grey lighten-3';
	}

	getTrialStatusColor(cust: Customer): string {
		const { progress, subscriptions } = cust;
		const daysLeft = this.getTrialDaysLeft(progress.trialEndAt);

		// Has a subscription
		if (subscriptions.length > 0) {
			if (this.hasActiveSubscription(subscriptions)) {
				return 'cyan darken-1';
			}
			return 'red darken-1';
		}

		// No subscription? Check if trial is running
		if (!progress.verified) {
			return 'blue-grey lighten-3';
		}

		if (daysLeft > 5) {
			return 'green darken-1';
		}

		if (daysLeft > 3) {
			return 'lime darken-1';
		}

		if (daysLeft > 0) {
			return 'amber darken-1';
		}

		return 'red darken-1';
	}

	getStatus(cust: Customer): string {
		let status = '';

		const { progress, subscriptions } = cust;

		// Has a subscription
		if (subscriptions.length > 0) {
			status = `${this.$t('general.customer')}`;
		}

		// No subscription? Check if trial is running
		if (progress.verified) {
			status = `${this.$t('general.email-verified')} [1/4]`;
		}

		if (progress.address) {
			status = `${this.$t('general.address-added')} [2/4]`;
		}

		if (progress.additionalInformation) {
			status = `${this.$t('general.info-added')} [3/4]`;
		}

		if (progress.permissionToPay) {
			status = `${this.$t('general.chose-plan')} [4/4]`;
		}

		if (status === '') status = `${this.$t('general.email-not-verified')} [0/4]`;

		return status;
	}

	getTrialPeriod(cust: Customer): string {
		const { progress, subscriptions } = cust;

		// Has a subscription
		if (subscriptions.length > 0) {
			if (this.hasActiveSubscription(subscriptions)) {
				return this.$t('general.active');
			}
			return this.$t('general.canceled');
		}

		const daysLeft = this.getTrialDaysLeft(progress.trialEndAt);
		const trialEnd = progress.trialEndAt;

		if (daysLeft > 0) {
			return `${this.$t('general.expires')} ${dayjs(trialEnd).from(dayjs(Date.now()))}`;
		}
		return `${this.$t('general.expired')} ${dayjs(trialEnd).from(dayjs(Date.now()))}`;
	}

	hasActiveSubscription(subscriptions: Subscription[]): boolean {
		for (let index = 0; index < subscriptions.length; index++) {
			if (subscriptions[index].status !== 'canceled') {
				return true;
			}
		}
		return false;
	}

	getActivityScore(used: any): number {
		let score = 0;

		if (used.qrCodes > 0) {
			if (used.qrCodes <= 2) {
				score += 1;
			} else if (used.qrCodes <= 5) {
				score += 2;
			} else {
				score += 5;
			}
		}

		if (used.scans > 0) {
			if (used.scans <= 5) {
				score += 1;
			} else if (used.scans <= 10) {
				score += 2;
			} else {
				score += 5;
			}
		}

		if (used.storage > 0) {
			score += 1;
		}

		if (used.users > 1) {
			score += 1;
		}

		return score;
	}

	getActivityColor(used: any): string {
		const score = this.getActivityScore(used);

		if (score > 10) {
			return 'red';
		}
		if (score > 5) {
			return 'orange';
		}
		if (score > 0) {
			return 'green';
		}

		// Score =< 0
		return 'grey';
	}

	getActivityText(used: any): string {
		const score = this.getActivityScore(used);

		if (score > 10) {
			return `${this.$t('levels.high')}`;
		}
		if (score > 5) {
			return `${this.$t('levels.medium')}`;
		}
		if (score > 0) {
			return `${this.$t('levels.low')}`;
		}

		// Score =< 0
		return `${this.$t('levels.none')}`;
	}

	newSearch(word: string | undefined): void {
		this.searchWord = word;

		if (word) {
			this.query.search = word;
		} else {
			delete this.query.search;
		}

		router.push({ query: this.query });
	}

	async getAll(): Promise<void> {
		this.loading = true;

		try {
			await customer.getAll(this.query);
		} catch (error) {
			notifier.error('getting', 'customers');
		} finally {
			this.stopLoading();
		}

		const parsedCustomerList: Array<any> = [];
		const customerList: Array<Customer> = customer.customers as Customer[];

		const promise: Promise<ResponseSingle<Object>>[] = [];
		// Add subscription and trial progress to customers;
		for (let i = 0; i < customerList.length; i++) {
			try {
				promise.push(this.getSub(customerList[i].id));
			} catch (error) {
				// error
			}
		}

		Promise.all(promise).then((values) => {
			for (let index = 0; index < values.length; index++) {
				const element: any = values[index];

				for (let i = 0; i < customerList.length; i++) {
					const cust = customerList[i];

					if (cust.id === element.id) {
						parsedCustomerList.push({ ...cust, subscriptions: element.subscriptions, progress: element.progress });
					}
				}
			}
		});

		this.customers = parsedCustomerList;
	}

	async getSub(id: ULID): Promise<any> {
		let subscriptions = (await customer.getSubscription(id)) as any;
		let progress: any = {};

		subscriptions = subscriptions.filter((item: any) => {
			const list: any = item.items.filter((list: any) => {
				return list.type === 'plan';
			});
			return list.length > 0;
		});

		if (subscriptions.length === 0) {
			progress = (await customer.getProgressCustomerID(id)).data;
		}

		return {
			id,
			subscriptions,
			progress
		};
	}

	get meta(): Meta {
		return customer.meta ? customer.meta : null;
	}

	stopLoading(): void {
		setTimeout(() => {
			this.loading = false;
		}, 200);
	}

	formatDate(date: string): string {
		if (!date) {
			return '';
		}
		return dayjs(date).format('DD MMM, YYYY | HH:mm');
	}

	popup(id: string): void {
		this.show = true;
		this.deleteId = id;
	}

	cancel(): void {
		this.show = false;
		this.deleteId = '';
	}

	confirm(): void {
		this.show = false;

		const id: number = parseInt(this.deleteId, 10);

		if (!id) {
			notifier.error('deleting', 'customer');
			return;
		}

		this.loading = true;

		customer
			.destroy(`/customers/${id}`)
			.then((): void => {
				this.loading = false;
				notifier.success('deleted', 'customer');
				this.cancel();
			})
			.catch((e): void => {
				this.loading = false;
				notifier.error('deleting', 'customer');
			});
	}

	goTo(id: number): void {
		router.push({ name: `customerDetailsEdit`, params: { Cid: id }, query: this.$route.query } as any);
	}
}
